import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { ReactQueryKeys } from '../../../Constants/ClientRoutingConstants';
import assetsApiService from '../Utilities/AssetsApiService';
import templatesApiService from '../../Templates/Utilities/TemplatesApiService';
import AssetTemplateListItemDto from '../../../Models/Assets/AssetTemplateListItemDto';
import AssetImageReadDto from '../../../Models/Assets/AssetImageReadDto';
import AreaReadDto from '../../../Models/Area/AreaReadDto';
import { getTemplateType, needsTemplates } from '../Utilities/AssetDetailsUtils';

export const useAssetDetailsQueries = (
  isEdit: boolean,
  machineryType: string,
  assetData: any,
  selectedTemplate: AssetTemplateListItemDto | null
) => {
  const locationsQuery = useQuery<Array<AreaReadDto>>(
    ReactQueryKeys.AllAreasQuery,
    () => assetsApiService.getAllLocations()
  );

  const templatesQuery = useQuery<AssetTemplateListItemDto[]>(
    ReactQueryKeys.AllTemplatesQuery,
    () => templatesApiService.getTemplatesAssets(),
    {
      enabled: !isEdit && needsTemplates(machineryType),
      keepPreviousData: true,
    }
  );

  const templateType = getTemplateType(machineryType); // "hme" | "loco"
  const filteredTemplates = useMemo(
    () =>
      (templatesQuery.data ?? []).filter(
        (t) => templateType && t.type?.toLowerCase() === templateType
      ),
    [templatesQuery.data, templateType]
  );

  const selectedTemplateDetailQuery = useQuery(
    [ReactQueryKeys.SelectedTemplateQuery, selectedTemplate?.id],
    () => templatesApiService.getTemplateAsset(selectedTemplate!.id),
    { enabled: !!selectedTemplate?.id && !isEdit }
  );

  const imageId = isEdit
    ? assetData?.assetTemplate?.imageId
    : selectedTemplateDetailQuery.data?.imageId;
  const assetImageQuery = useQuery<AssetImageReadDto>(
    [ReactQueryKeys.AllAssetImagesQuery, imageId],
    () => {
      if (!imageId) throw new Error('No image ID available');
      return assetsApiService.getAssetImages(imageId);
    },
    {
      enabled: !!imageId,
    }
  );

  return {
    locationsQuery,
    templatesQuery: {
      ...templatesQuery,
      data: filteredTemplates,
    },
    assetImageQuery,
  };
};

